import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ArrowRight, Crop, Zap, Shield, Download } from 'lucide-react'
import { useIntl } from 'react-intl'
import { useLanguage } from '../contexts/LanguageContext'

const Hero = () => {
  const intl = useIntl()
  const { getLocalizedPath } = useLanguage()

  const features = [
    {
      icon: <Zap className="h-6 w-6" />,
      title: intl.formatMessage({ id: 'hero.features.fast.title' }),
      description: intl.formatMessage({ id: 'hero.features.fast.description' })
    },
    {
      icon: <Shield className="h-6 w-6" />,
      title: intl.formatMessage({ id: 'hero.features.secure.title' }),
      description: intl.formatMessage({ id: 'hero.features.secure.description' })
    },
    {
      icon: <Download className="h-6 w-6" />,
      title: intl.formatMessage({ id: 'hero.features.formats.title' }),
      description: intl.formatMessage({ id: 'hero.features.formats.description' })
    }
  ]

  return (
    <section className="relative min-h-screen flex items-center gradient-bg overflow-hidden pt-20 sm:pt-16">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-float"></div>
        <div className="absolute top-40 right-10 w-72 h-72 bg-secondary-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-float" style={{ animationDelay: '2s' }}></div>
        <div className="absolute -bottom-8 left-20 w-72 h-72 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-float" style={{ animationDelay: '4s' }}></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-20 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
          {/* Left Column - Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center lg:text-left"
          >
            <motion.h1 
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-gray-900 mb-4 sm:mb-6 leading-tight"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              {intl.formatMessage({ id: 'hero.title' })}{' '}
              <span className="text-gradient block sm:inline">{intl.formatMessage({ id: 'hero.titleHighlight' })}</span>{' '}
              <span className="block sm:inline">{intl.formatMessage({ id: 'hero.titleSuffix' })}</span>
            </motion.h1>
            
            <motion.p 
              className="text-lg sm:text-xl text-gray-600 mb-6 sm:mb-8 max-w-2xl leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              {intl.formatMessage({ id: 'hero.description' })}
            </motion.p>

            <motion.div 
              className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-8 sm:mb-12"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <Link to={getLocalizedPath('/cropper')} className="btn-primary flex items-center justify-center space-x-2 text-base sm:text-lg py-3 sm:py-4 px-6 sm:px-8">
                <Crop className="h-4 w-4 sm:h-5 sm:w-5" />
                <span>{intl.formatMessage({ id: 'hero.startCropping' })}</span>
                <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5" />
              </Link>
              <Link to={getLocalizedPath('/faq')} className="btn-secondary flex items-center justify-center space-x-2 text-base sm:text-lg py-3 sm:py-4 px-6 sm:px-8">
                <span>{intl.formatMessage({ id: 'hero.learnMore' })}</span>
              </Link>
            </motion.div>

            {/* Features */}
            <motion.div 
              className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              {features.map((feature, index) => (
                <motion.div 
                  key={index} 
                  className="flex items-start sm:items-center space-x-3 text-left p-3 sm:p-0 rounded-lg bg-white/50 sm:bg-transparent"
                  whileHover={{ scale: 1.02 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <div className="flex-shrink-0 p-2 sm:p-2 bg-primary-100 rounded-lg text-primary-600">
                    {feature.icon}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-gray-900 text-sm sm:text-base">{feature.title}</h3>
                    <p className="text-xs sm:text-sm text-gray-600 mt-1 leading-relaxed">{feature.description}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right Column - Visual */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative mt-8 lg:mt-0"
          >
            <div className="relative mx-auto max-w-xs sm:max-w-sm md:max-w-md">
              {/* Main Avatar Preview */}
              <motion.div 
                className="relative bg-white rounded-2xl shadow-2xl p-6 sm:p-8"
                whileHover={{ scale: 1.02, y: -5 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="aspect-square bg-gradient-to-br from-primary-400 to-secondary-400 rounded-full mb-4 sm:mb-6 relative overflow-hidden">
                  <div className="absolute inset-3 sm:inset-4 bg-white rounded-full flex items-center justify-center">
                    <Crop className="h-12 w-12 sm:h-16 sm:w-16 text-primary-600" />
                  </div>
                </div>
                <div className="text-center">
                  <h3 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">{intl.formatMessage({ id: 'hero.preview.title' })}</h3>
                  <p className="text-xs sm:text-sm text-gray-600">{intl.formatMessage({ id: 'hero.preview.description' })}</p>
                </div>
              </motion.div>

              {/* Floating Elements */}
              <motion.div 
                className="absolute -top-2 -right-2 sm:-top-4 sm:-right-4 bg-green-500 text-white p-2 sm:p-3 rounded-full shadow-lg"
                animate={{ y: [-5, 5, -5] }}
                transition={{ duration: 4, repeat: Infinity }}
              >
                <Shield className="h-4 w-4 sm:h-6 sm:w-6" />
              </motion.div>
              
              <motion.div 
                className="absolute -bottom-2 -left-2 sm:-bottom-4 sm:-left-4 bg-blue-500 text-white p-2 sm:p-3 rounded-full shadow-lg"
                animate={{ y: [5, -5, 5] }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                <Zap className="h-4 w-4 sm:h-6 sm:w-6" />
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

export default Hero